import { createContext, useContext, useState, useEffect, useRef } from "react";
import { CONFIG, PRICING, colors, storage, scorePersonality, scoreCore, scoreLikert } from "../data/config";

// ============================================================
// APP CONTEXT — All shared state and functions
// Firebase is lazily imported inside effects/handlers, never at top level.
// This means the Firebase SDK is NOT loaded on landing page visits.
// ============================================================

const AppContext = createContext(null);

export function useApp() {
  return useContext(AppContext);
}

export function AppProvider({ children }) {
  // --- STATE ---
  const [screen, setScreen] = useState("landing");
  const [currentTest, setCurrentTest] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [results, setResults] = useState(null);
  const [userInfo, setUserInfo] = useState({ name: "", email: "", gender: "", age: "", profession: "", industry: "", education: "", nationality: "" });
  const [pendingUserInfo, setPendingUserInfo] = useState({ name: "", email: "" });
  const [aiReport, setAiReport] = useState(null);
  const [aiReportFull, setAiReportFull] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paidTests, setPaidTests] = useState([]);
  const [discountCode, setDiscountCode] = useState("");
  const [discountApplied, setDiscountApplied] = useState(0);
  const [allUserData, setAllUserData] = useState([]);
  const [adminCodes, setAdminCodes] = useState(CONFIG.DISCOUNT_CODES);
  const [adminPassword, setAdminPassword] = useState("");
  const [adminLoggedIn, setAdminLoggedIn] = useState(false);
  const [savedResults, setSavedResults] = useState({});
  const [countdown, setCountdown] = useState(() => {
    const endStored = storage.get("countdown_end");
    const endTime = endStored ? new Date(endStored) : new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
    const diff = endTime - new Date();
    if (diff <= 0) return { days: 0, hours: 0, mins: 0, secs: 0 };
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      mins: Math.floor((diff % 3600000) / 60000),
      secs: Math.floor((diff % 60000) / 1000),
    };
  });
  const [purchaseTarget, setPurchaseTarget] = useState(null);
  const [notification, setNotification] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [pendingScreenAfterAuth, setPendingScreenAfterAuth] = useState(null);

  // Keep a ref to savedResults for use inside async handlers without stale closure
  const savedResultsRef = useRef(savedResults);
  useEffect(() => { savedResultsRef.current = savedResults; }, [savedResults]);

  // --- COUNTDOWN TIMER ---
  useEffect(() => {
    let endDate = storage.get("countdown_end");
    if (!endDate || new Date(endDate) < new Date()) {
      endDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
      storage.set("countdown_end", endDate);
    }
    const endTime = new Date(endDate);
    const timer = setInterval(() => {
      const diff = endTime - new Date();
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, mins: 0, secs: 0 });
      } else {
        setCountdown({
          days: Math.floor(diff / 86400000),
          hours: Math.floor((diff % 86400000) / 3600000),
          mins: Math.floor((diff % 3600000) / 60000),
          secs: Math.floor((diff % 60000) / 1000),
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // --- LOAD STORED DATA ON MOUNT (non-Firebase) ---
  useEffect(() => {
    const codes = storage.get("admin-discount-codes");
    if (codes) setAdminCodes(codes);
    const users = storage.get("all-user-data");
    if (users) setAllUserData(users);
    // Then try Firestore async (may overwrite)
    (async () => {
      try {
        const { getDiscountCodes, getAllUsers } = await import("../firestore");
        const firestoreCodes = await getDiscountCodes();
        if (firestoreCodes) setAdminCodes(firestoreCodes);
        const firestoreUsers = await getAllUsers();
        if (firestoreUsers && firestoreUsers.length > 0) setAllUserData(firestoreUsers);
      } catch (e) { console.log("Firestore load failed, using localStorage"); }
    })();
  }, []);

  // --- AUTH STATE LISTENER (lazy Firebase, deferred until first interaction) ---
  // Firebase injects a 94KB auth iframe on init — defer until user interacts
  // so it doesn't pollute the page-load performance window.
  const authInitDone = useRef(false);
  const authUnsub = useRef(() => {});

  function initAuth() {
    if (authInitDone.current) return;
    authInitDone.current = true;
    setAuthLoading(true);
    Promise.all([
      import("../firebase"),
      import("firebase/auth"),
    ]).then(async ([{ auth }, { onAuthStateChanged }]) => {
      authUnsub.current = onAuthStateChanged(auth, async (user) => {
        if (user) {
          setAuthUser(user);
          const profile = await loadUserResults(user.email);
          if (profile) {
            if (profile.name) setUserInfo(prev => ({ ...prev, name: profile.name, email: user.email }));
            if (profile.paidTests) setPaidTests(profile.paidTests);
          }
          setUserInfo(prev => ({ ...prev, email: user.email, name: prev.name || user.displayName || "" }));
        } else {
          setAuthUser(null);
        }
        setAuthLoading(false);
      });
    }).catch(() => {
      setAuthLoading(false);
    });
  }

  // Trigger on first user interaction (mousemove fires within ms on any device)
  useEffect(() => {
    const events = ["mousemove", "touchstart", "click", "keydown"];
    const handler = () => {
      events.forEach(e => window.removeEventListener(e, handler));
      initAuth();
    };
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      authUnsub.current();
    };
  }, []);

  // If user navigates to an auth-required screen before interacting, init immediately
  const AUTH_SCREENS_CTX = ["your-journey", "paid-report", "admin"];
  useEffect(() => {
    if (AUTH_SCREENS_CTX.includes(screen)) initAuth();
  }, [screen]);

  // --- USER DATA FUNCTIONS ---
  async function loadUserResults(email) {
    try {
      const { getUser } = await import("../firestore");
      const user = await getUser(email);
      if (user) {
        setSavedResults(user);
        setPaidTests(user.paidTests || []);
        return user;
      }
    } catch (e) { console.log("Firestore unavailable, using localStorage"); }
    const key = `user-results-${email.toLowerCase().replace(/[^a-z0-9]/g, "_")}`;
    const data = storage.get(key);
    if (data) {
      setSavedResults(data);
      setPaidTests(data.paidTests || []);
      return data;
    }
    return null;
  }

  async function saveUserResultsData(email, data) {
    try {
      const { saveUser } = await import("../firestore");
      await saveUser({ email, ...data });
    } catch (e) { console.log("Firestore save failed"); }
    const key = `user-results-${email.toLowerCase().replace(/[^a-z0-9]/g, "_")}`;
    const existing = savedResultsRef.current || {};
    const merged = { ...existing, ...data, paidTests: [...new Set([...(existing.paidTests || []), ...(data.paidTests || [])])] };
    storage.set(key, merged);
    setSavedResults(merged);
  }

  async function saveToAdminData(userData) {
    try {
      const { saveUser } = await import("../firestore");
      await saveUser(userData);
    } catch (e) { console.log("Firestore admin save failed"); }
    const existing = allUserData || [];
    const idx = existing.findIndex((u) => u.email === userData.email);
    if (idx >= 0) existing[idx] = { ...existing[idx], ...userData };
    else existing.push(userData);
    storage.set("all-user-data", existing);
    setAllUserData([...existing]);
  }

  // --- NOTIFICATION ---
  function showNotify(msg, type = "success") {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3500);
  }

  // --- AUTH FUNCTIONS ---
  async function signInWithGoogle() {
    try {
      const [{ auth, googleProvider }, { signInWithPopup }] = await Promise.all([
        import("../firebase"),
        import("firebase/auth"),
      ]);
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      setAuthUser(user);
      const name = user.displayName || "";
      const email = user.email;
      setUserInfo(prev => ({ ...prev, name, email }));
      const { saveUserProfile } = await import("../firestore");
      await saveUserProfile(email, {
        name,
        email,
        photoURL: user.photoURL,
        uid: user.uid,
        lastSeen: new Date().toISOString()
      });
      await loadUserResults(email);
      showNotify(`Welcome, ${name.split(" ")[0]}!`);
      if (pendingScreenAfterAuth) {
        setScreen(pendingScreenAfterAuth);
        setPendingScreenAfterAuth(null);
      } else {
        setScreen("your-journey");
      }
      return user;
    } catch (err) {
      console.error("Google sign-in failed:", err);
      showNotify("Sign-in failed. Please try again.", "error");
      return null;
    }
  }

  async function signOutUser() {
    const [{ auth }, { signOut }] = await Promise.all([
      import("../firebase"),
      import("firebase/auth"),
    ]);
    await signOut(auth);
    setAuthUser(null);
    setUserInfo({ name: "", email: "", gender: "", age: "", profession: "", industry: "", education: "", nationality: "" });
    setPaidTests([]);
    setSavedResults({});
    setScreen("landing");
    showNotify("Signed out successfully.");
  }

  // --- AI REPORT GENERATION ---
  async function generateReport(testType, scores, detailed = false) {
    setLoading(true);
    const name = userInfo.name?.trim() || pendingUserInfo.name?.trim() || "Friend";

    try {
      const { buildPrompt } = await import("../data/prompts");
      const prompt = buildPrompt(testType, scores, name, detailed);
      const controller = new AbortController();
      const fetchTimeout = setTimeout(() => controller.abort(), 270000);

      let response;
      try {
        response = await fetch(CONFIG.API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "generate-report", prompt, detailed }),
          signal: controller.signal,
        });
      } finally {
        clearTimeout(fetchTimeout);
      }

      if (!response.ok) throw new Error(`API error: ${response.status}`);

      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("text/event-stream")) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullText = "";
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop();
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const data = line.slice(6).trim();
            if (data === "[DONE]") { setLoading(false); return fullText; }
            if (data.startsWith("[ERROR]")) throw new Error(data.replace("[ERROR] ", ""));
            try {
              const chunk = JSON.parse(data);
              fullText += chunk;
              if (detailed) setAiReportFull(fullText);
              else setAiReport(fullText);
            } catch (_) { /* ignore malformed chunk */ }
          }
        }
        setLoading(false);
        return fullText;
      }

      const data = await response.json();
      setLoading(false);
      if (data.report) return data.report;
      throw new Error("No report in response");

    } catch (error) {
      console.error("AI generation error:", error);
      setLoading(false);
      if (detailed) {
        const msg = error.name === "AbortError"
          ? "Report generation timed out. Please try again."
          : "AI report generation failed. Please try again.";
        showNotify(msg, "error");
        throw error;
      }
      showNotify("Using preview template...");
      const { generateFallbackReport } = await import("../data/prompts");
      return generateFallbackReport(testType, scores, name, detailed);
    }
  }

  // --- PAYMENT ---
  function handlePayment(testType, amount) {
    if (!window.Razorpay) {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => openRazorpay(testType, amount);
      document.body.appendChild(script);
    } else {
      openRazorpay(testType, amount);
    }
  }

  function openRazorpay(testType, amount) {
    const options = {
      key: CONFIG.RAZORPAY_KEY,
      amount: amount * 100,
      currency: "INR",
      name: "Mirror Mirror",
      description: `${PRICING[testType]?.label || "Assessment"} - Detailed Report`,
      handler: async function (response) {
        const paymentId = response.razorpay_payment_id;
        showNotify("Payment successful! Generating your report...");
        const newPaid = [...new Set([...paidTests, testType])];
        setPaidTests(newPaid);
        saveUserResultsData(userInfo.email, { paidTests: newPaid, paymentId, paymentTest: testType, paymentAmount: amount });
        saveToAdminData({ ...userInfo, paidTests: newPaid, paymentId, paymentTest: testType, paymentAmount: amount, timestamp: new Date().toISOString() });

        if (testType === "bundle") {
          const allTests = ["personality", "core", "strengths", "shadow", "values"];
          setPaidTests(allTests);
          saveUserResultsData(userInfo.email, { paidTests: allTests });
          showNotify("Bundle unlocked! All assessments are now available.");
          setScreen("your-journey");
        } else {
          try {
            const report = await generateReport(testType, results, true);
            setAiReportFull(report);
            setScreen("paid-report");
            const labelMap = { personality: "My Personality", core: "My Core", strengths: "My Strengths", shadow: "My Shadow", values: "My Values & Drivers" };
            sendReportByEmail(report, `${labelMap[testType] || testType} - Detailed Report`);
            saveUserResultsData(userInfo.email, { [`${testType}Report`]: report });
          } catch (err) {
            console.error("Paid report generation failed:", err);
            showNotify("Report generation failed. Please contact support.", "error");
          }
        }
      },
      prefill: { name: userInfo.name, email: userInfo.email },
      theme: { color: colors.primary },
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  }

  function applyDiscount(code) {
    const upper = code.toUpperCase();
    if (adminCodes[upper]) {
      setDiscountApplied(adminCodes[upper]);
      showNotify(`Discount code applied! ₹${adminCodes[upper]} off`);
    } else {
      showNotify("Invalid discount code", "error");
      setDiscountApplied(0);
    }
  }

  function getPrice(testType) {
    const base = PRICING[testType]?.price || 0;
    const discount = testType === "bundle" ? discountApplied * 2 : discountApplied;
    return Math.max(0, base - discount);
  }

  // --- QUIZ FLOW ---
  function startTest(testType) {
    const advancedTests = ["strengths", "shadow", "values"];
    const hasBaseData = savedResults?.personalityScores || savedResults?.coreScores;
    if (advancedTests.includes(testType) && !hasBaseData) {
      setPurchaseTarget(testType);
      setScreen("step-nudge");
      return;
    }
    const saved = storage.get(`quiz_progress_${testType}`);
    if (saved && saved.answers?.length > 0) {
      const resume = window.confirm(`You have ${saved.answers.length} answers saved for this assessment. Resume where you left off?`);
      if (resume) {
        setCurrentTest(testType);
        setAnswers(saved.answers);
        setQuestionIndex(saved.answers.length);
        setResults(null);
        setAiReport(null);
        setAiReportFull(null);
        setScreen("test");
        return;
      }
    }
    storage.delete(`quiz_progress_${testType}`);
    setCurrentTest(testType);
    setQuestionIndex(0);
    setAnswers([]);
    setResults(null);
    setAiReport(null);
    setAiReportFull(null);
    setScreen("test");
  }

  function retakeTest(testType) {
    storage.delete(`quiz_progress_${testType}`);
    setCurrentTest(testType);
    setQuestionIndex(0);
    setAnswers([]);
    setResults(null);
    setAiReport(null);
    setAiReportFull(null);
    setScreen("test");
  }

  async function handleAnswer(answer) {
    // Import questions lazily — only needed when a quiz is active
    const {
      PERSONALITY_QUESTIONS, CORE_QUESTIONS,
      STRENGTHS_QUESTIONS, SHADOW_QUESTIONS, VALUES_QUESTIONS, MBTI_TYPES
    } = await import("../data/questions");

    const questionsMap = {
      personality: PERSONALITY_QUESTIONS,
      core: CORE_QUESTIONS,
      strengths: STRENGTHS_QUESTIONS,
      shadow: SHADOW_QUESTIONS,
      values: VALUES_QUESTIONS,
    };
    const questions = questionsMap[currentTest] || [];

    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);
    storage.set(`quiz_progress_${currentTest}`, { answers: newAnswers });

    if (newAnswers.length >= questions.length) {
      storage.delete(`quiz_progress_${currentTest}`);
      let scores;
      if (currentTest === "personality") scores = scorePersonality(newAnswers, PERSONALITY_QUESTIONS, MBTI_TYPES);
      else if (currentTest === "core") scores = scoreCore(newAnswers, CORE_QUESTIONS);
      else if (currentTest === "strengths") scores = scoreLikert(newAnswers, STRENGTHS_QUESTIONS, ["leadership", "creativity", "analytical", "empathy", "resilience", "communication", "strategic", "adaptability"]);
      else if (currentTest === "shadow") scores = scoreLikert(newAnswers, SHADOW_QUESTIONS, ["perfectionism", "control", "avoidance", "people_pleasing", "cynicism", "impulsivity", "rigidity", "detachment"]);
      else if (currentTest === "values") scores = scoreLikert(newAnswers, VALUES_QUESTIONS, ["achievement", "autonomy", "connection", "security", "impact", "growth", "recognition", "adventure"]);
      setResults(scores);

      if (!authUser) {
        setPendingScreenAfterAuth("collect-name");
        setScreen("google-signin");
      } else {
        setUserInfo(prev => ({ ...prev, name: authUser.displayName || prev.name, email: authUser.email || prev.email }));
        setLoading(true);
        setScreen("free-results");
        const report = await generateReport(currentTest, scores, false);
        setAiReport(report);
        saveUserResultsData(authUser.email, { [`${currentTest}Scores`]: scores, [`${currentTest}Preview`]: report });
        const freeLabel1 = currentTest === "personality" ? "My Personality" : currentTest === "core" ? "My Core" : currentTest === "strengths" ? "My Strengths" : currentTest === "shadow" ? "My Shadow" : "My Values & Drivers";
        sendReportByEmail(report, `${freeLabel1} - Free Preview`, authUser.email, authUser.displayName);
        setLoading(false);
      }
    } else {
      setQuestionIndex(newAnswers.length);
    }
  }

  async function handleNameSubmit() {
    if (!pendingUserInfo.name.trim() || !pendingUserInfo.email.trim()) {
      showNotify("Please enter both name and email", "error");
      return;
    }
    const updatedUserInfo = { ...userInfo, name: pendingUserInfo.name.trim(), email: pendingUserInfo.email.trim() };
    setUserInfo(updatedUserInfo);
    localStorage.setItem("mirror_email", pendingUserInfo.email.trim());
    localStorage.setItem("mirror_user", JSON.stringify(updatedUserInfo));

    setLoading(true);
    const report = await generateReport(currentTest, results, false);
    setAiReport(report);
    setScreen("free-results");
    saveUserResultsData(pendingUserInfo.email, { [`${currentTest}Scores`]: results, [`${currentTest}Preview`]: report });
    const freeLabel2 = currentTest === "personality" ? "My Personality" : currentTest === "core" ? "My Core" : currentTest === "strengths" ? "My Strengths" : currentTest === "shadow" ? "My Shadow" : "My Values & Drivers";
    sendReportByEmail(report, `${freeLabel2} - Free Preview`, pendingUserInfo.email, pendingUserInfo.name);
    setLoading(false);
  }

  // --- REPORT RENDERING ---
  function renderReportToJSX(text) {
    if (!text) return null;
    const lines = text.split('\n');
    return lines.map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <div key={i} style={{ height: 8 }} />;
      if (/^[-*━═]{3,}$/.test(trimmed)) return <hr key={i} style={{ border: "none", borderTop: "1px solid #d0dce8", margin: "12px 0" }} />;
      if (/TAP.*UNLOCK|UNLOCK.*BELOW/i.test(trimmed)) return (
        <div key={i} style={{ marginTop: 8, marginBottom: 8 }}>
          <a href="https://mirrormirror.life" style={{ color: "#1E88E5", fontWeight: 600, fontSize: 15 }}>Visit MirrorMirror.life to unlock your Full Report</a>
        </div>
      );
      if (/^#{4,}\s/.test(trimmed)) {
        const clean = trimmed.replace(/^#{4,}\s+/, '').replace(/\*\*/g, '');
        return <div key={i} style={{ fontSize: 14, fontWeight: 700, color: "#1565C0", marginTop: 14, marginBottom: 4 }}>{clean}</div>;
      }
      if (/^#{2}\s/.test(trimmed) && !/^#{3,}/.test(trimmed)) {
        const clean = trimmed.replace(/^#{2}\s+/, '').replace(/\*\*/g, '');
        return <div key={i} style={{ fontSize: 18, fontWeight: 700, color: "#0d47a1", marginTop: 28, marginBottom: 10, paddingBottom: 6, borderBottom: "2px solid #1E88E5" }}>{clean}</div>;
      }
      if (/^#{3}\s/.test(trimmed) && !/^#{4,}/.test(trimmed)) {
        const clean = trimmed.replace(/^#{3}\s+/, '').replace(/\*\*/g, '');
        return <div key={i} style={{ fontSize: 16, fontWeight: 700, color: "#1565C0", marginTop: 18, marginBottom: 6 }}>{clean}</div>;
      }
      if (/^#\s/.test(trimmed)) {
        const clean = trimmed.replace(/^#\s+/, '').replace(/\*\*/g, '');
        return <div key={i} style={{ fontSize: 20, fontWeight: 700, color: "#0d47a1", marginTop: 28, marginBottom: 12 }}>{clean}</div>;
      }
      if (/^\*\*[^*]+\*\*\.?:?$/.test(trimmed)) {
        const clean = trimmed.replace(/\*\*/g, '');
        return <div key={i} style={{ fontSize: 15, fontWeight: 700, color: "#1565C0", marginTop: 14, marginBottom: 4 }}>{clean}</div>;
      }
      if (/^[*\-▸•]\s/.test(trimmed) || /^\d+\.\s/.test(trimmed)) {
        const rest = trimmed.replace(/^([*\-▸•]|\d+\.)\s+/, '').replace(/\*\*/g, '');
        return (
          <div key={i} style={{ display: "flex", gap: 10, paddingLeft: 8, marginBottom: 6 }}>
            <span style={{ color: "#1E88E5", flexShrink: 0 }}>•</span>
            <span style={{ color: "#091a2a", lineHeight: 1.7 }}>{rest}</span>
          </div>
        );
      }
      const parts = trimmed.split(/(\*\*[^*]+\*\*)/g);
      const rendered = parts.map((part, j) =>
        /^\*\*[^*]+\*\*$/.test(part)
          ? <strong key={j} style={{ color: "#1565C0" }}>{part.replace(/\*\*/g, '')}</strong>
          : part
      );
      return <p key={i} style={{ margin: "0 0 10px 0", color: "#091a2a", lineHeight: 1.8, fontSize: 16 }}>{rendered}</p>;
    });
  }

  function renderMarkdownToPDF(doc, text, margin, maxWidth, startY, pageH) {
    let y = startY;
    let isFirstSection = true;
    let l1Counter = 0;
    let sectionCount = 0;
    function newPage() { doc.addPage(); y = 52; }
    function checkPage() { if (y > pageH - 70) { newPage(); } }
    function sanitize(str) {
      return str
        .replace(/[\u{1F000}-\u{1FFFF}]/gu, "")
        .replace(/[☀-➿]/g, "")
        .replace(/[━═╔╗╚╝║╠╣╦╩╬�]/g, "")
        .replace(/▸/g, "-")
        .trim();
    }
    const lines = text.split("\n");
    for (let i = 0; i < lines.length; i++) {
      const raw = lines[i];
      const trimmed = raw.trim();
      const leading = raw.length - raw.trimStart().length;
      if (!trimmed) { y += 6; continue; }
      if (/TAP.*UNLOCK|UNLOCK.*BELOW/i.test(trimmed)) {
        y += 6; checkPage();
        const linkText = "Visit MirrorMirror.life to unlock your Full Report";
        doc.setFontSize(11); doc.setFont("helvetica", "normal"); doc.setTextColor(30, 136, 229);
        doc.text(linkText, margin, y);
        doc.link(margin, y - 11, doc.getTextWidth(linkText), 14, { url: "https://mirrormirror.life" });
        y += 18; continue;
      }
      if (/^[-*━═�]{3,}$/.test(trimmed) || (trimmed.length > 10 && (trimmed.match(/[━═]/g)||[]).length / trimmed.length > 0.6)) {
        doc.setDrawColor(30, 136, 229); doc.setLineWidth(0.6);
        doc.line(margin, y - 2, margin + maxWidth, y - 2); y += 12;
        continue;
      }
      if (/REPORT IS READY/i.test(trimmed)) {
        const clean = sanitize(trimmed.replace(/\*\*/g, ""));
        if (!clean) continue;
        y += 6; checkPage();
        doc.setFontSize(13); doc.setFont("helvetica", "bold"); doc.setTextColor(13, 71, 161);
        const wrapped = doc.splitTextToSize(clean, maxWidth);
        for (const wl of wrapped) { checkPage(); doc.text(wl, margin, y); y += 20; }
        continue;
      }
      if (/^#{4,}\s/.test(trimmed)) {
        const clean = sanitize(trimmed.replace(/^#{4,}\s+/, "").replace(/\*\*/g, ""));
        if (!clean) continue;
        l1Counter = 0;
        y += 6; checkPage();
        doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(13, 71, 161);
        const wrapped = doc.splitTextToSize(clean, maxWidth);
        for (const wl of wrapped) { checkPage(); doc.text(wl, margin, y); y += 16; }
        continue;
      }
      if (/^#{2}\s/.test(trimmed) && !/^#{3,}/.test(trimmed)) {
        if (!isFirstSection) { newPage(); } else { isFirstSection = false; }
        sectionCount++; l1Counter = 0;
        const clean = sanitize(trimmed.replace(/^#{2}\s+/, "").replace(/\*\*/g, ""));
        doc.setFontSize(15); doc.setFont("helvetica", "bold"); doc.setTextColor(13, 71, 161);
        const wrapped = doc.splitTextToSize(clean, maxWidth);
        for (const wl of wrapped) { checkPage(); doc.text(wl, margin, y); y += 22; }
        y += 4; continue;
      }
      if (/^#{3}\s/.test(trimmed) && !/^#{4,}/.test(trimmed)) {
        if (sectionCount === 2) { newPage(); } else { y += 8; checkPage(); }
        l1Counter = 0;
        const clean = sanitize(trimmed.replace(/^#{3}\s+/, "").replace(/\*\*/g, ""));
        if (!clean) continue;
        doc.setFontSize(13); doc.setFont("helvetica", "bold"); doc.setTextColor(13, 71, 161);
        const wrapped = doc.splitTextToSize(clean, maxWidth);
        for (const wl of wrapped) { checkPage(); doc.text(wl, margin, y); y += 20; }
        y += 4; continue;
      }
      if (/^#\s/.test(trimmed)) {
        if (!isFirstSection) { newPage(); } else { isFirstSection = false; }
        sectionCount++; l1Counter = 0;
        const clean = sanitize(trimmed.replace(/^#\s+/, "").replace(/\*\*/g, ""));
        doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.setTextColor(13, 71, 161);
        const wrapped = doc.splitTextToSize(clean, maxWidth);
        for (const wl of wrapped) { checkPage(); doc.text(wl, margin, y); y += 23; }
        y += 4; continue;
      }
      if (/^\*\*[^*]+\*\*\.?:?$/.test(trimmed)) {
        const clean = sanitize(trimmed.replace(/\*\*/g, ""));
        if (!clean) continue;
        l1Counter = 0;
        y += 5; checkPage();
        doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(13, 71, 161);
        const wrapped = doc.splitTextToSize(clean, maxWidth);
        for (const wl of wrapped) { checkPage(); doc.text(wl, margin, y); y += 16; }
        continue;
      }
      if (leading >= 2 && /^([*\-▸•]|\d+\.)\s/.test(trimmed)) {
        const rest = sanitize(trimmed.replace(/^([*\-▸•]|\d+\.)\s+/, "").replace(/\*\*/g, ""));
        if (!rest) continue;
        checkPage();
        const ix = margin + 24;
        doc.setFontSize(11); doc.setFont("helvetica", "normal"); doc.setTextColor(20, 30, 48);
        doc.text("•", ix, y);
        const wrapped = doc.splitTextToSize(rest, maxWidth - 36);
        for (const wl of wrapped) { checkPage(); doc.text(wl, ix + 12, y); y += 15; }
        continue;
      }
      if (leading === 0 && /^\d+\.\s/.test(trimmed)) {
        const match = trimmed.match(/^(\d+)\.\s+(.*)/);
        const num = parseInt(match[1]);
        const rest = sanitize(match[2].replace(/\*\*/g, ""));
        if (!rest) continue;
        l1Counter = num;
        y += 2; checkPage();
        doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(13, 71, 161);
        doc.text(`${num}.`, margin, y);
        doc.setFont("helvetica", "normal"); doc.setTextColor(20, 30, 48);
        const wrapped = doc.splitTextToSize(rest, maxWidth - 22);
        for (const wl of wrapped) { checkPage(); doc.text(wl, margin + 22, y); y += 15; }
        continue;
      }
      if (leading === 0 && /^[*\-▸•]\s/.test(trimmed)) {
        l1Counter++;
        const rest = sanitize(trimmed.replace(/^[*\-▸•]\s+/, "").replace(/\*\*/g, ""));
        if (!rest) continue;
        y += 2; checkPage();
        doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(13, 71, 161);
        doc.text(`${l1Counter}.`, margin, y);
        doc.setFont("helvetica", "normal"); doc.setTextColor(20, 30, 48);
        const wrapped = doc.splitTextToSize(rest, maxWidth - 22);
        for (const wl of wrapped) { checkPage(); doc.text(wl, margin + 22, y); y += 15; }
        continue;
      }
      const clean = sanitize(trimmed.replace(/\*\*/g, ""));
      if (clean) {
        checkPage();
        doc.setFontSize(11); doc.setFont("helvetica", "normal"); doc.setTextColor(20, 30, 48);
        const wrapped = doc.splitTextToSize(clean, maxWidth);
        for (const wl of wrapped) { checkPage(); doc.text(wl, margin, y); y += 15; }
      }
    }
    return y;
  }

  function downloadReport(reportText, title) {
    // currentTest and results come from closure
    const _currentTest = currentTest;
    const _results = results;
    const _userName = userInfo.name;

    function buildPDF() {
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 52;
      const maxWidth = pageW - margin * 2;

      doc.setFillColor(7, 29, 82);
      doc.rect(0, 0, pageW, pageH, "F");
      doc.setFillColor(30, 136, 229);
      doc.rect(0, pageH * 0.38, pageW, 4, "F");
      doc.setFontSize(11); doc.setFont("helvetica", "bold");
      doc.setTextColor(150, 190, 255);
      doc.text("MIRROR MIRROR", pageW / 2, 80, { align: "center" });
      doc.setFontSize(9); doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 140, 200);
      doc.text("Scientific Self-Reflection  |  MirrorMirror.life", pageW / 2, 96, { align: "center" });

      if (_currentTest === "personality" && _results?.type) {
        doc.setFontSize(72); doc.setFont("helvetica", "bold");
        doc.setTextColor(30, 136, 229);
        doc.text(_results.type, pageW / 2, pageH * 0.32, { align: "center" });
        doc.setFontSize(14); doc.setFont("helvetica", "normal");
        doc.setTextColor(200, 220, 255);
        const desc = (_results.description || "").split(" - ")[0];
        if (desc) doc.text(desc, pageW / 2, pageH * 0.32 + 28, { align: "center" });
      }

      doc.setFontSize(22); doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      const wrappedTitle = doc.splitTextToSize(title, pageW - 80);
      let ty = pageH * 0.5;
      wrappedTitle.forEach(line => { doc.text(line, pageW / 2, ty, { align: "center" }); ty += 30; });

      doc.setFontSize(12); doc.setFont("helvetica", "normal");
      doc.setTextColor(160, 200, 255);
      doc.text(`Prepared for  ${_userName || "You"}`, pageW / 2, pageH * 0.66, { align: "center" });
      doc.setFontSize(10);
      doc.setTextColor(100, 140, 180);
      doc.text(new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" }), pageW / 2, pageH * 0.66 + 18, { align: "center" });

      doc.setFontSize(8); doc.setTextColor(80, 110, 160);
      doc.text("Mirror Mirror is a self-awareness tool, not a clinical assessment.", pageW / 2, pageH - 36, { align: "center" });

      doc.addPage();
      let y = 64;

      doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.setTextColor(9, 26, 42);
      doc.text("MIRROR MIRROR", margin, y); y += 18;
      doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(90, 106, 122);
      doc.text("Scientific Self-Reflection  |  MirrorMirror.life", margin, y); y += 10;
      doc.setDrawColor(30, 136, 229); doc.setLineWidth(1.5);
      doc.line(margin, y, pageW - margin, y); y += 18;

      doc.setFontSize(14); doc.setFont("helvetica", "bold"); doc.setTextColor(9, 26, 42);
      const wrTitle = doc.splitTextToSize(title, maxWidth);
      wrTitle.forEach(l => { doc.text(l, margin, y); y += 20; });

      doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(90, 106, 122);
      doc.text(`Prepared for: ${_userName || "User"}  |  ${new Date().toLocaleDateString()}`, margin, y); y += 18;
      doc.setDrawColor(200, 210, 220); doc.setLineWidth(0.5);
      doc.line(margin, y, pageW - margin, y); y += 20;

      renderMarkdownToPDF(doc, reportText, margin, maxWidth, y, pageH);

      const pageCount = doc.internal.getNumberOfPages();
      for (let p = 1; p <= pageCount; p++) {
        doc.setPage(p);
        doc.setFontSize(8); doc.setTextColor(150, 160, 170);
        doc.text("Mirror Mirror is a self-awareness tool, not a clinical assessment.  Visit MirrorMirror.life", margin, pageH - 28);
        doc.text(`Page ${p} of ${pageCount}`, pageW - margin - 40, pageH - 28);
      }

      doc.save(`MirrorMirror_${title.replace(/\s+/g, "_")}_${(_userName || "Report").replace(/\s+/g, "_")}.pdf`);
    }

    if (window.jspdf) { buildPDF(); }
    else {
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
      script.onload = buildPDF;
      document.body.appendChild(script);
    }
  }

  function sendReportByEmail(reportText, title, emailOverride, nameOverride) {
    const _userInfo = userInfo;
    function dispatch() {
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 48;
      const maxWidth = pageW - margin * 2;
      let y = 64;

      const recipientEmail = emailOverride || _userInfo.email;
      const recipientName = nameOverride || _userInfo.name || "Friend";

      doc.setFontSize(18); doc.setFont("helvetica", "bold"); doc.setTextColor(9, 26, 42);
      doc.text("MIRROR MIRROR", margin, y); y += 20;
      doc.setFontSize(10); doc.setFont("helvetica", "normal"); doc.setTextColor(90, 106, 122);
      doc.text("Scientific Self-Reflection  |  MirrorMirror.life", margin, y); y += 12;
      doc.setDrawColor(30, 136, 229); doc.setLineWidth(1.5);
      doc.line(margin, y, pageW - margin, y); y += 22;
      doc.setFontSize(15); doc.setFont("helvetica", "bold"); doc.setTextColor(9, 26, 42);
      doc.text(title, margin, y); y += 20;
      doc.setFontSize(10); doc.setFont("helvetica", "normal"); doc.setTextColor(90, 106, 122);
      doc.text(`Prepared for: ${recipientName}`, margin, y); y += 14;
      doc.text(`Date: ${new Date().toLocaleDateString()}`, margin, y); y += 16;
      doc.setDrawColor(200, 210, 220); doc.setLineWidth(0.5);
      doc.line(margin, y, pageW - margin, y); y += 20;
      renderMarkdownToPDF(doc, reportText, margin, maxWidth, y, pageH);

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8); doc.setTextColor(150, 160, 170);
        doc.text("Mirror Mirror is a self-awareness tool, not a clinical assessment.  Visit MirrorMirror.life", margin, pageH - 28);
        doc.text(`Page ${i} of ${pageCount}`, pageW - margin - 40, pageH - 28);
      }

      const pdfBase64 = doc.output("datauristring");
      fetch(CONFIG.API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "send-report-email", email: recipientEmail, name: recipientName, title, pdfBase64 }),
      })
        .then((r) => r.json())
        .then(() => showNotify(`Report emailed to ${recipientEmail}`))
        .catch((err) => console.error("Email delivery failed:", err));
    }

    if (window.jspdf) { dispatch(); }
    else {
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
      script.onload = dispatch;
      document.body.appendChild(script);
    }
  }

  // --- CONTEXT VALUE ---
  const value = {
    // State
    screen, setScreen,
    currentTest, setCurrentTest,
    questionIndex, setQuestionIndex,
    answers, setAnswers,
    results, setResults,
    userInfo, setUserInfo,
    pendingUserInfo, setPendingUserInfo,
    aiReport, setAiReport,
    aiReportFull, setAiReportFull,
    loading, setLoading,
    paidTests, setPaidTests,
    discountCode, setDiscountCode,
    discountApplied, setDiscountApplied,
    allUserData, setAllUserData,
    adminCodes, setAdminCodes,
    adminPassword, setAdminPassword,
    adminLoggedIn, setAdminLoggedIn,
    savedResults, setSavedResults,
    countdown,
    purchaseTarget, setPurchaseTarget,
    notification, setNotification,
    authUser, setAuthUser,
    authLoading,
    pendingScreenAfterAuth, setPendingScreenAfterAuth,
    // Functions
    showNotify,
    signInWithGoogle,
    signOutUser,
    generateReport,
    handlePayment,
    openRazorpay,
    applyDiscount,
    getPrice,
    startTest,
    retakeTest,
    handleAnswer,
    handleNameSubmit,
    renderReportToJSX,
    renderMarkdownToPDF,
    downloadReport,
    sendReportByEmail,
    loadUserResults,
    saveUserResultsData,
    saveToAdminData,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
